/**
 * Canonical AI-credits 402 detection — one normalizer for EVERY backend shape.
 *
 * The platform returns an "insufficient credits" 402 in several historical
 * shapes (audited): the orchestrator's shared handler
 * (`{error:"insufficient_ai_credits", required, available, feature, purchase_url}`),
 * its meter/reserve route (`{detail:{error:"insufficient_credits", ...}}`),
 * content-service (`{detail:{code:"insufficient_credits", ...}}`), and
 * b2b/memory/rag (`{detail:{message, required, available, feature}}` with no
 * marker at all). Every AI-credit consumer must react the same way, so this
 * module flattens all of them into one {@link NormalizedInsufficientCredits}.
 *
 * Pure (no imports from ./client) so it can be used from the API client itself
 * without an import cycle. The `ApiError`-aware guard lives in the banner.
 */

/** The unified, flattened insufficient-credits payload. */
export interface NormalizedInsufficientCredits {
  feature?: string;
  required?: number;
  available?: number;
  purchaseUrl?: string;
  /** Human-facing message (French), if the backend supplied one. */
  detail?: string;
  /**
   * When the blocked call was persisted as a resumable run (L-resume), its id.
   * The CreditGuardHost stores this so it can offer "Recharger et continuer"
   * (POST /api/v1/runs/{id}/retry) after the top-up.
   */
  blockedRunId?: string;
  /**
   * Why the call was blocked (from the 402 `error`/`code`):
   *  - `insufficient`        - the tenant wallet is empty -> top up.
   *  - `user_budget_exceeded`- the wallet HAS funds but this member hit their
   *    personal monthly cap -> ask an admin (buying credits would not help).
   *  - `engine_unavailable`  - the wallet could not be verified (fail-closed
   *    engine error) -> retry shortly; the balance is unknown, not empty.
   */
  code?: CreditsBlockCode;
  /** Personal monthly cap / used (only for `user_budget_exceeded`). */
  cap?: number;
  used?: number;
}

export type CreditsBlockCode = 'insufficient' | 'user_budget_exceeded' | 'engine_unavailable';

/** Window event dispatched on any insufficient-credits 402 (CreditGuardHost listens). */
export const AI_CREDITS_INSUFFICIENT_EVENT = 'psn:ai-credits-insufficient';

const INSUFFICIENT_MARKERS = new Set([
  'insufficient_ai_credits',
  'insufficient_credits',
  'user_budget_exceeded',
  'ai_credits_engine_unavailable',
]);

function asNum(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function asStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Detect + flatten an insufficient-credits 402 from ANY service shape. Returns
 * the normalized payload, or `null` when (status, body) is not a credit
 * shortfall. Safe to call with anything.
 */
export function parseInsufficientCredits(
  status: number,
  body: unknown,
): NormalizedInsufficientCredits | null {
  if (status !== 402 || !body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const detailObj =
    b.detail && typeof b.detail === 'object' ? (b.detail as Record<string, unknown>) : null;

  const marker =
    asStr(b.error) ??
    asStr(b.code) ??
    (detailObj ? asStr(detailObj.error) ?? asStr(detailObj.code) : undefined);

  const required = asNum(b.required) ?? (detailObj ? asNum(detailObj.required) : undefined);
  const available = asNum(b.available) ?? (detailObj ? asNum(detailObj.available) : undefined);

  // A credit shortfall is either explicitly marked, OR a 402 that carries both
  // `required` and `available` numbers (HTTP 402 is used ONLY for credits on
  // this platform — see the audit), which covers the marker-less b2b/rag shape.
  const isShortfall =
    (marker != null && INSUFFICIENT_MARKERS.has(marker)) ||
    (required != null && available != null);
  if (!isShortfall) return null;

  const detail =
    asStr(b.detail) ?? // `detail` is a plain string (FastAPI HTTPException(detail="…"))
    (detailObj ? asStr(detailObj.message) ?? asStr(detailObj.detail) : undefined) ??
    asStr(b.message);
  const feature = asStr(b.feature) ?? (detailObj ? asStr(detailObj.feature) : undefined);
  const purchaseUrl =
    asStr(b.purchase_url) ?? (detailObj ? asStr(detailObj.purchase_url) : undefined);
  const blockedRunId =
    asStr(b.blocked_run_id) ?? (detailObj ? asStr(detailObj.blocked_run_id) : undefined);

  const reason = asStr(b.reason) ?? (detailObj ? asStr(detailObj.reason) : undefined);
  const code: CreditsBlockCode =
    marker === 'user_budget_exceeded' || reason === 'user_budget_exceeded'
      ? 'user_budget_exceeded'
      : marker === 'ai_credits_engine_unavailable' || reason === 'fail_closed_engine_error'
        ? 'engine_unavailable'
        : 'insufficient';
  const cap = asNum(b.cap) ?? (detailObj ? asNum(detailObj.cap) : undefined);
  const used = asNum(b.used) ?? (detailObj ? asNum(detailObj.used) : undefined);

  return { feature, required, available, purchaseUrl, detail, blockedRunId, code, cap, used };
}

/**
 * localStorage key holding the id of the last credit-blocked run (L-resume).
 * Survives the Stripe redirect (full page reload) so the settings page can
 * offer "Recharger et continuer" for that exact run after the top-up.
 */
export const BLOCKED_RUN_STORAGE_KEY = 'psn:blocked-run-id';

/** Persist a blocked run id for the post-top-up resume flow (best-effort). */
export function rememberBlockedRun(runId: string | undefined | null): void {
  if (typeof window === 'undefined' || !runId) return;
  try {
    window.localStorage.setItem(BLOCKED_RUN_STORAGE_KEY, runId);
  } catch {
    /* storage unavailable — resume simply won't be offered */
  }
}

/** Read (and clear) the stored blocked run id, if any. */
export function takeBlockedRun(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = window.localStorage.getItem(BLOCKED_RUN_STORAGE_KEY);
    if (id) window.localStorage.removeItem(BLOCKED_RUN_STORAGE_KEY);
    return id;
  } catch {
    return null;
  }
}

// --- Credits charged (success path) -------------------------------------------

/** What an AI action just cost (server-stamped on the response: `credits`). */
export interface CreditsCharged {
  charged: number;
  /** Wallet balance right after the settle (null when the server could not say). */
  balanceAfter: number | null;
  features: string[];
}

/** Window event dispatched when ANY response reports credits spent (CreditGuardHost listens). */
export const AI_CREDITS_CHARGED_EVENT = 'psn:ai-credits-charged';

/**
 * Read the `credits: {charged, balance_after, features}` stamp the backend adds
 * to every response whose request settled AI credits (JSON body) — falls back to
 * the `X-AI-Credits-*` headers for non-object bodies. Returns null when nothing
 * was charged. Pure; safe with anything.
 */
export function parseCreditsCharged(body: unknown, headers?: Headers): CreditsCharged | null {
  const raw =
    body && typeof body === 'object' && !Array.isArray(body)
      ? (body as Record<string, unknown>).credits
      : undefined;
  if (raw && typeof raw === 'object') {
    const c = raw as Record<string, unknown>;
    const charged = asNum(c.charged);
    if (charged != null && charged > 0) {
      return {
        charged,
        balanceAfter: asNum(c.balance_after) ?? null,
        features: Array.isArray(c.features)
          ? c.features.filter((f): f is string => typeof f === 'string')
          : [],
      };
    }
    return null;
  }
  const hCharged = headers?.get('X-AI-Credits-Charged');
  if (hCharged) {
    const charged = Number(hCharged);
    if (Number.isFinite(charged) && charged > 0) {
      const hBal = headers?.get('X-AI-Credits-Balance-After');
      const bal = hBal != null && hBal !== '' ? Number(hBal) : NaN;
      return { charged, balanceAfter: Number.isFinite(bal) ? bal : null, features: [] };
    }
  }
  return null;
}

/** Dispatch the global credits-charged event (no-op outside a browser). */
export function emitCreditsCharged(payload: CreditsCharged): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(AI_CREDITS_CHARGED_EVENT, { detail: payload }));
  } catch {
    /* CustomEvent unavailable — the balance badge still catches up on its next refresh */
  }
}

/**
 * A BULK AI result that stopped part-way for credits (HTTP 200, not 402):
 * `{status:"partial", incomplete_reason:"insufficient_credits",
 *   insufficient_credits:{required, available, balance, purchase_url, ...}}`
 * (community bulk drafts / targeting suggestions / theme refresh). Returns the
 * normalized payload — same shape as a 402 — or `null` when the body is not a
 * credit-stopped partial result.
 */
export function parsePartialCreditStop(body: unknown): NormalizedInsufficientCredits | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (b.incomplete_reason !== 'insufficient_credits') return null;
  const blk =
    b.insufficient_credits && typeof b.insufficient_credits === 'object'
      ? (b.insufficient_credits as Record<string, unknown>)
      : {};
  return {
    feature: asStr(blk.feature),
    required: asNum(blk.required),
    available: asNum(blk.available),
    purchaseUrl: asStr(blk.purchase_url),
    detail: asStr(blk.message),
    code: 'insufficient',
  };
}

/** Dispatch the global insufficient-credits event (no-op outside a browser). */
export function emitInsufficientCredits(payload: NormalizedInsufficientCredits): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(AI_CREDITS_INSUFFICIENT_EVENT, { detail: payload }));
  } catch {
    /* CustomEvent unavailable — the thrown ApiError still surfaces at the call site */
  }
}

// --- Calls that cannot go through apiFetch (XHR uploads, raw fetch) -----------

/**
 * Give a response that bypassed `apiFetch` (an XHR upload with progress, a raw
 * `fetch`) the SAME credit handling apiFetch applies: a 402 shortfall raises the
 * global « Crédits insuffisants » dialog, a success carrying the `credits` stamp
 * (body or X-AI-Credits-* headers) updates the header balance + toasts the spend.
 * Returns the normalized shortfall (or null) so the caller can still throw.
 */
export function noteAiCreditsResponse(
  status: number,
  body: unknown,
  headers?: Headers,
): NormalizedInsufficientCredits | null {
  if (status === 402) {
    const insufficient = parseInsufficientCredits(status, body);
    if (insufficient) emitInsufficientCredits(insufficient);
    return insufficient;
  }
  if (status >= 200 && status < 300) {
    const charged = parseCreditsCharged(body, headers);
    if (charged) emitCreditsCharged(charged);
  }
  return null;
}

/**
 * {@link noteAiCreditsResponse} for a finished XHR: parses its JSON body (null
 * when not JSON) and its X-AI-Credits-* headers. Returns the parsed body and the
 * shortfall (non-null on a credits 402 — reject with `new ApiError(402, body)`
 * so call sites' creditErrorText() recognises it).
 */
export function noteXhrAiCredits(
  xhr: Pick<XMLHttpRequest, 'status' | 'responseText' | 'getResponseHeader'>,
): { body: unknown; insufficient: NormalizedInsufficientCredits | null } {
  let body: unknown = null;
  try {
    body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
  } catch {
    body = null;
  }
  const insufficient = noteAiCreditsResponse(xhr.status, body, xhrCreditHeaders(xhr));
  return { body, insufficient };
}

/** The X-AI-Credits-* response headers of an XHR, as a Headers (for {@link noteAiCreditsResponse}). */
export function xhrCreditHeaders(xhr: Pick<XMLHttpRequest, 'getResponseHeader'>): Headers {
  const h = new Headers();
  for (const name of ['X-AI-Credits-Charged', 'X-AI-Credits-Balance-After']) {
    const v = xhr.getResponseHeader(name);
    if (v != null) h.set(name, v);
  }
  return h;
}
